import {
	CellType,
	TvmFormDefinition,
	TvmFormFieldDefinition,
	TvmFieldType,
} from "./form-definition";
import InputForm from "../input-form";
import useForm from "../../../hooks/use-form";
import {
	IonButton,
	IonCard,
	IonCardContent,
	IonCardHeader,
	IonCardTitle,
} from "@ionic/react";
import { useTranslation } from "react-i18next";
import { evaluateExpression } from "./utils";
import InfoImg from "../../info-image";
import { isNil } from "ramda";
import { useContext } from "react";
import { LocalizationContext } from "../../../App";
import { translate } from "../../../utils/helpers";
import { required } from "../../../utils/validators";

import "./styles.css";

export interface TvmFormProps {
	title: string;
	systemName: string;
	topImage?: string;
	definition: TvmFormDefinition;
	fieldValues?: { [key: string]: any };
	submitButtonText?: string;
	onSubmit: Function;
	validate?: boolean;
}

// TODO: Consider inputs of type select
const TvmForm: React.FC<TvmFormProps> = ({
	title,
	systemName,
	topImage,
	definition,
	submitButtonText,
	fieldValues,
	onSubmit,
	validate = true,
}: TvmFormProps) => {
	const { t } = useTranslation();
	const {
		formState: { state },
		errors = {},
		validate: validateFormData,
		valid,
		handleUpdates,
		handleSubmit,
	} = useForm(
		Object.keys(definition.fields || {})
			.filter((k) => definition.fields[k].inputType !== "range-bar")
			.reduce(
				(acc, k) => ({
					...acc,
					[k]: required,
				}),
				{} as any
			),
		{
			...(fieldValues && {
				initialState: fieldValues,
			}),
		}
	);

	const handleInputChanges =
		(fieldDef: TvmFormFieldDefinition, updateHandler: Function) =>
		(inputValue: string | boolean | number) => {
			let result: string | number | boolean = inputValue;

			if (fieldDef.inputType === "number" && typeof result === "string") {
				result = result.includes(".") ? parseFloat(result) : parseInt(result);
			}
			updateHandler(result);
		};

	const tableResultBuilder = (row, inputData) =>
		typeof row === "string"
			? row
			: Object.keys(row).reduce(
					(acc, k) => ({
						...acc,
						[k]: {
							...row[k],
							value:
								row[k]?.type === CellType.EXPRESSION
									? evaluateExpression(row[k]?.value || "", inputData)
									: row[k]?.value,
						},
					}),
					{}
			  );
	const handleFormSubmit = (data) => {
		if (validate && !validateFormData().valid) {
			return;
		}
		if (!definition.formula) {
			onSubmit(data);
		}

		const computedData = Object.keys(definition.fields).reduce((acc, k) => {
			const fieldName = definition.fields[k].name || k;
			const emptyField = isNil(data[fieldName]) || isNaN(data[fieldName]);
			const isRangeBarField = definition.fields[k].inputType === "range-bar";

			return {
				...acc,
				[fieldName]:
					emptyField && isRangeBarField
						? definition.fields[k].value
						: data[fieldName],
			};
		}, {});

		let evalResult = Object.keys(definition.formula).reduce((acc, k) => {
			if (k === "__cond") {
				const formulaObj = definition.formula["__cond"];

				for (const exp of Object.keys(formulaObj)) {
					const result = evaluateExpression(exp, computedData);

					if (result) {
						return {
							...acc,
							__cond: {
								result: tableResultBuilder(formulaObj[exp], computedData),
							},
						};
					}
				}
				return { ...acc, __cond: null };
			}
			const result = evaluateExpression(definition.formula[k], computedData);

			return {
				...acc,
				[k]: result,
			};
		}, {});
		let evalFlagsResult = definition.flags
			? Object.keys(definition.flags).reduce((acc, k) => {
					const flagObj = definition.flags![k];
					const result = evaluateExpression(flagObj.expression, computedData);

					return {
						...acc,
						[k]: result,
					};
			  }, {})
			: null;

		onSubmit({
			...computedData,
			_evaluationResult: evalResult,
			_flags: evalFlagsResult,
		});
	};

	const locales = useContext(LocalizationContext);

	return (
		<IonCard>
			{topImage && <InfoImg imgSrc={topImage} />}
			<IonCardHeader>
				{/* <IonCardSubtitle>{systemName.toUpperCase()}</IonCardSubtitle> */}
				<IonCardTitle>{title}</IonCardTitle>
			</IonCardHeader>
			<IonCardContent>
				<div className="formCont">
					{Object.keys(definition.fields)
						.sort((k1, k2) => {
							const f1 = definition.fields[k1];
							const f2 = definition.fields[k2];

							if (!isNil(f1.index) && !isNil(f2.index)) {
								return f1.index! < f2.index! ? -1 : 1;
							}
							if (f1.name && f2.name) {
								return f1.name < f2.name ? -1 : 1;
							}
							return k1 < k2 ? -1 : 1;
						})
						.map((k) => ({
							key: definition.fields[k].name || k,
							field: definition.fields[k],
						}))
						.map(({ key, field }, idx) => (
							<div key={idx} style={{ marginBottom: 20 }}>
								<InputForm
									{...(field.type === TvmFieldType.SELECT && {
										options: field.selectOptions!.map((s) => ({
											...s,
											label: translate(s.label, locales),
										})),
									})}
									value={state[key]}
									defaultValue={field.value}
									id={key}
									label={translate(field.label, locales)}
									type={field.inputType}
									rangeProps={field.range}
									error={errors[key]}
									onValueChange={handleInputChanges(field, handleUpdates(key))}
								/>
							</div>
						))}
					{!valid && (
						<p style={{ textAlign: "center", marginBottom: 10, color: "red" }}>
							{t("validation.error")}
						</p>
					)}
				</div>
				<div className="ion-text-center">
					<IonButton onClick={handleSubmit(handleFormSubmit)}>
						{/*
							TODO: Choose better default text for submit button
						*/}
						{submitButtonText || t("general.calculate")}
					</IonButton>
				</div>
			</IonCardContent>
		</IonCard>
	);
};

export default TvmForm;
